import { useMemo, useState } from "react";
import { Plus, Pencil, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtINR } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, Badge, EmptyState } from "@/components/common/States";
import { UserFormDialog } from "./UserFormDialog";

const PAGE = 15;

export default function UsersPage() {
  const { can, companyId, branches, me } = useSession();
  const enabled = can("user.view") && !!companyId;
  const { data, loading, error, reload } = useFetch("/users", null, enabled);
  const { data: catalog } = useFetch("/meta/rbac", null, enabled);
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const rows = useMemo(() => (data || []).filter((u) =>
    (role === "all" || u.role === role) && (status === "all" || u.status === status) &&
    `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())), [data, q, role, status]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice((page - 1) * PAGE, page * PAGE);
  const branchName = (id) => branches.find((b) => b.id === id)?.code || "—";

  if (!can("user.view")) return <NoAccess perm="user.view" />;

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="Administration" title="Users" subtitle="Staff accounts, roles, branch access and approval limits."
        actions={can("user.manage") && companyId && <Button onClick={() => setDialog({ open: true, user: null })} data-testid="add-user-button"><Plus className="h-4 w-4 mr-1.5" />Add user</Button>} />
      {!companyId ? (
        <div className="g39-card"><EmptyState title="Select a company" body="Users belong to a company. Choose one from the top bar." /></div>
      ) : (
        <div className="g39-card overflow-hidden">
          <div className="flex flex-col md:flex-row gap-3 p-4 border-b bg-secondary/40">
            <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9 bg-white" placeholder="Search name or email" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="user-search-input" /></div>
            <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
              <SelectTrigger className="md:w-52 bg-white" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All roles</SelectItem>{(catalog?.roles || []).filter((r) => r.key !== "platform_admin").map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="md:w-40 bg-white" data-testid="user-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{["all", "active", "invited", "inactive"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {loading && <LoadingRows />}
          {error && <ErrorState message={error} onRetry={reload} />}
          {data && !rows.length && <EmptyState title="No users found" body="Add staff so they can sign in to this company." />}
          {shown.length > 0 && (
            <div className="overflow-x-auto">
              <Table data-testid="users-table">
                <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Branches</TableHead><TableHead>Approval limit</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {shown.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                      <TableCell><div className="font-medium">{u.name} {u.id === me.user.id && <span className="text-xs text-gold">(you)</span>}</div><div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                      <TableCell className="text-sm">{u.role_label}</TableCell>
                      <TableCell className="text-xs font-mono-g">{u.all_branches ? <Badge tone="gold">All branches</Badge> : u.branch_ids.map(branchName).join(", ")}</TableCell>
                      <TableCell className="text-sm">{fmtINR(u.approval_limit)}</TableCell>
                      <TableCell><Badge tone={u.status} data-testid={`user-status-${u.email}`}>{u.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        {can("user.manage") && <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, user: u })} data-testid={`edit-user-${u.email}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {rows.length > PAGE && (
            <div className="flex items-center justify-between px-4 py-3 border-t text-sm" data-testid="users-pagination">
              <span className="text-muted-foreground">{rows.length} users · page {page} of {pages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" disabled={page === pages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </div>
      )}
      <UserFormDialog open={dialog.open} user={dialog.user} catalog={catalog} users={data} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={reload} />
    </div>
  );
}
